import { useMemo } from 'react';
import { MenuPlacement, MenuPosition } from 'react-select';
import { MODAL_BODY_CLASS, MODAL_CONTAINER_CLASS, useModalLayer } from 'ud-ui/components/modal/modal-layer-context';

export interface ModalMenuPortalProps {
  menuPortalTarget?: HTMLElement | null;
  menuPosition?: MenuPosition;
  menuPlacement?: MenuPlacement;
  closeMenuOnScroll?: (event: Event) => boolean;
}

/**
 * Список прокручивается внутри своей выдачи — это не повод закрывать. Закрываем, только когда
 * прокрутилась панель окна (белое окно или `Modal.Body`): поле уехало, а список остался бы
 * висеть на прежнем месте поверх шапки.
 */
function isModalPaneScroll(event: Event): boolean {
  const target = event.target;
  return target instanceof Element && target.matches(`.${MODAL_CONTAINER_CLASS}, .${MODAL_BODY_CLASS}`);
}

/**
 * `closeMenuOnScroll` отдаётся и вне окна: react-select подписывается на прокрутку только при
 * монтировании, а `dialog` в контексте появляется на рендер позже селекта (узел окна известен
 * после первого прохода). Вне окна панелей окна нет — функция всегда отвечает `false`.
 */
const NO_PORTAL: ModalMenuPortalProps = { closeMenuOnScroll: isModalPaneScroll };

/**
 * Пропсы react-select, с которыми список внутри `UDModal` открывается поверх окна.
 *
 * Окно — нативный `<dialog>` в верхнем слое: список в `body` оказался бы под ним и не нажимался,
 * а список на месте упирается в низ прокручиваемого тела. Поэтому внутри окна список выносится
 * порталом в сам `<dialog>`, позиционируется `fixed` (окно растянуто на экран, а `absolute`
 * добавил бы прокрутку страницы, запертую на время окна), у края экрана открывается вверх и
 * закрывается при прокрутке тела окна (react-select не следит за прокруткой предков,
 * JedWatson/react-select#3734, #4085).
 *
 * Вне окна — только `closeMenuOnScroll`, который там ничего не закрывает: поведение списков не меняется. Пропсы кладутся **до** пропсов
 * вызывающей стороны, чтобы явно переданные побеждали.
 */
export function useModalMenuPortal(): ModalMenuPortalProps {
  const { dialog } = useModalLayer();

  return useMemo(
    () =>
      dialog
        ? {
            menuPortalTarget: dialog,
            menuPosition: 'fixed',
            menuPlacement: 'auto',
            closeMenuOnScroll: isModalPaneScroll,
          }
        : NO_PORTAL,
    [dialog],
  );
}
